import { useState, useCallback, useEffect } from 'react';
import { Input } from './Input';
import { ValidationResult } from '../../utils/validation';

interface ValidatedInputProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  validate?: (value: string) => ValidationResult;
  validateOnBlur?: boolean;
  validateOnChange?: boolean;
  hint?: string;
  placeholder?: string;
  disabled?: boolean;
  type?: string;
  className?: string;
}

export function ValidatedInput({
  label,
  value,
  onChange,
  validate,
  validateOnBlur = true,
  validateOnChange = false,
  hint,
  placeholder,
  disabled,
  type = 'text',
  className,
}: ValidatedInputProps) {
  const [error, setError] = useState<string | undefined>();
  const [touched, setTouched] = useState(false);

  const runValidation = useCallback(
    (val: string) => {
      if (validate) {
        const result = validate(val);
        setError(result.valid ? undefined : result.error);
        return result.valid;
      }
      return true;
    },
    [validate]
  );

  // Validate on mount if value exists
  useEffect(() => {
    if (value && touched) {
      runValidation(value);
    }
  }, [value, touched, runValidation]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);

    if (validateOnChange && touched) {
      runValidation(newValue);
    }
  };

  const handleBlur = () => {
    setTouched(true);
    if (validateOnBlur) {
      runValidation(value);
    }
  };

  return (
    <Input
      type={type}
      label={label}
      value={value}
      onChange={handleChange}
      onBlur={handleBlur}
      error={touched ? error : undefined}
      hint={hint}
      placeholder={placeholder}
      disabled={disabled}
      className={className}
    />
  );
}
